/**
 * "Preparing your download…" (§23).
 *
 * Sits alongside the upload dock rather than blocking the screen. It no longer
 * promises you can leave, though — the archive is assembled in this tab now,
 * because the server has no key to open the photographs with. Saying so is the
 * whole job of the hint below: a download that dies silently when somebody
 * navigates away is worse than one that asks them to stay.
 */

import { formatBytes } from "@fc/core";
import { Button } from "../../components/ui/Button.js";
import type { ExportState } from "./useExport.js";
import "./ExportDock.css";

export interface ExportDockProps extends ExportState {
  onSave: () => void;
  onDismiss: () => void;
}

export function ExportDock({
  state,
  progress,
  itemCount,
  totalBytes,
  error,
  skipped,
  onDismiss,
}: ExportDockProps) {
  if (state === "idle") return null;

  const preparing = state === "queued" || state === "active";

  return (
    <div
      className="fc-export"
      data-state={state === "failed" || state === "expired" ? "error" : state}
      role="status"
      aria-live="polite"
    >
      <div className="fc-export__head">
        <strong>
          {preparing && "Building your download…"}
          {state === "ready" && "Your download is ready"}
          {state === "failed" && "We couldn't build that download"}
          {state === "expired" && "That download has expired"}
        </strong>
        {preparing && <span className="fc-tabular">{progress}%</span>}
      </div>

      {preparing && (
        <div className="fc-export__track">
          <div
            className="fc-export__fill"
            style={{ transform: `scaleX(${progress / 100})` }}
          />
        </div>
      )}

      <p className="fc-export__hint">
        {error ??
          (state === "ready"
            ? `${itemCount} ${itemCount === 1 ? "item" : "items"}${
                totalBytes > 0 ? ` · ${formatBytes(totalBytes)}` : ""
              }`
            : `${itemCount} ${itemCount === 1 ? "item" : "items"} · keep this tab open`)}
      </p>

      {/*
        Named, not counted. “Three photos were skipped” sends somebody back
        through a hundred thumbnails trying to work out which three; the names
        are what let them check whether it mattered.
      */}
      {skipped.length > 0 && (
        <ul className="fc-export__skipped">
          {skipped.slice(0, 5).map((item) => (
            <li key={item.name}>
              {item.name} — {item.why}
            </li>
          ))}
          {skipped.length > 5 && (
            <li>and {skipped.length - 5} more</li>
          )}
        </ul>
      )}

      <div className="fc-export__actions">
        {/*
          No Save button. The file is written as it is built — straight to
          disk where the browser allows it, handed over the moment the last
          entry goes in where it does not — so by the time this says “ready”
          the download has already happened.
        */}
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          {preparing ? "Stop" : "Dismiss"}
        </Button>
      </div>
    </div>
  );
}
